import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { WorkspaceRef } from "@/api/workspace";

/** A session's markdown drafts, read, saved and rendered from the app
 *  (``app/api/sessions/_document_drafts.py``).
 *
 *  ``name`` is the flat ``<slug>.md`` file name the ``draft_ready`` card
 *  carries. ``/sessions/{id}/drafts`` belongs to the composer drafts,
 *  hence ``document-drafts``. */

export type DraftParse = {
  ok: boolean;
  error: { line: number; message: string } | null;
  warnings: string[];
};

export type DraftLastEdit = {
  revision_id: string;
  by: string | null;
  by_user_id: string | null;
  at: string;
  lines_added: number;
  lines_removed: number;
};

/** Why a draft can't be edited now. The first four are the file itself
 *  (``content`` is null then), the rest the viewer and the moment. */
export type DraftReason =
  | "too_large"
  | "not_utf8"
  | "binary"
  | "not_regular_file"
  | "not_participant"
  | "org_membership_required"
  | "session_archived"
  | "session_failed"
  | "turn_in_progress";

export type DocumentDraft = {
  name: string;
  /** ``drafts/<name>``, relative to the session. */
  path: string;
  content: string | null;
  sha256: string | null;
  size_bytes: number;
  max_bytes: number;
  editable: boolean;
  reason: DraftReason | string | null;
  parse: DraftParse | null;
  last_edit: DraftLastEdit | null;
};

export type SavedDraft = {
  name: string;
  path: string;
  sha256: string;
  size_bytes: number;
  /** ``null`` for a save that changed nothing. */
  revision_id: string | null;
  lines_added: number;
  lines_removed: number;
  parse: DraftParse;
};

export type DraftRenderJob = {
  job_id: string;
  state: "queued";
  source_draft: string;
  sha256: string;
};

/** What opens the draft editor: a ``draft_ready`` card, the transcript's
 *  draft events or the workspace tree's drafts folder. ``file`` is the
 *  draft in the workspace API (the download link and the file viewer),
 *  ``null`` when there's none to offer; ``mode`` picks the tab the
 *  editor opens on. */
export type OpenDraftRequest = {
  name: string;
  file: WorkspaceRef | null;
  title: string | null;
  mode: "read" | "edit";
};

/** Transcript event kinds about drafts edited and rendered in the app
 *  (``features/drafts/DraftEventCard`` words them). */
export const DRAFT_EVENT_KINDS: ReadonlySet<string> = new Set([
  "draft_edited",
  "draft_rendered",
  "draft_edits_replaced",
]);

export const draftKeys = {
  /** Under the session's key, so a ``session.updated`` refetches an open
   *  draft too: a turn starting or ending changes whether it's editable. */
  all: (sessionId: string) => ["session", sessionId, "document-draft"] as const,
  draft: (sessionId: string, name: string) =>
    ["session", sessionId, "document-draft", name] as const,
};

/** A draft's title as the chat shows it. Cards stored before the title
 *  came bare carry an English " (markdown draft)" suffix, in every UI
 *  language; the card already says it's a draft, in the reader's own. */
export function draftDisplayTitle(title: string): string {
  return title.replace(/ \(markdown draft\)$/, "");
}

/** The API's own rule for a draft it reads and saves (``store._NAME_RE``):
 *  a flat ``<name>.md`` of ASCII letters, digits, ``.``, ``_`` and ``-``,
 *  lower-case ``.md``. Any other name it answers 400. */
const EDITABLE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,200}\.md$/;

export function isEditableDraftName(name: string): boolean {
  return EDITABLE_NAME.test(name);
}

/** The draft's file name from its path (``offer.md``, or the
 *  ``/…/drafts/offer.md`` a card stored before the API named roots holds). */
export function draftNameFromPath(path: string): string {
  return path.split(/[\\/]/).pop() ?? path;
}

export function draftUrl(sessionId: string, name: string): string {
  return `/sessions/${sessionId}/document-drafts/${encodeURIComponent(name)}`;
}

export function useDocumentDraft(sessionId: string, name: string | null) {
  return useQuery({
    queryKey: draftKeys.draft(sessionId, name ?? ""),
    queryFn: async () =>
      (await api.get<DocumentDraft>(draftUrl(sessionId, name ?? ""))).data,
    enabled: Boolean(sessionId && name),
    // The file changes under the viewer (the agent, another participant):
    // always ask again when an editor opens.
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
  });
}

/** Save ``content`` over the text whose sha256 the edit started from.
 *  A draft that changed since answers 412 (``draft_changed``). */
export async function saveDocumentDraft(
  sessionId: string,
  name: string,
  content: string,
  baseSha256: string,
): Promise<SavedDraft> {
  const res = await api.put<SavedDraft>(
    draftUrl(sessionId, name),
    { content },
    { headers: { "If-Match": `"${baseSha256}"` } },
  );
  return res.data;
}

/** Queue a render of the saved draft; follow ``job_id`` on
 *  ``/api/document-jobs/{job_id}/stream``. */
export async function renderDocumentDraft(
  sessionId: string,
  name: string,
): Promise<DraftRenderJob> {
  return (await api.post<DraftRenderJob>(`${draftUrl(sessionId, name)}/render`)).data;
}
